import { useId } from 'react';
import { mergePickerItems, type PickerItem } from '@/components/modals/formPickerItems';
import React, { useState, useRef, useEffect } from 'react';
import { updateObjectInArray } from '../../../../../store/utility';
import { Field, Form, Formik } from 'formik';
import { Pencil, Plus } from 'lucide-react';
import { Dismiss16Regular } from '@fluentui/react-icons';
import * as Yup from 'yup';
import { InputField } from '../../../../../components/formFields/InputField';
import Bar from '../../../../../components/elements/Bar';
import Label from '@/components/Label';
import FormList from '../../../../../components/elements/FormList';
import PickValueWithClassificationModal from '../../../../../components/modals/PickValueWithClassificationModal';
import ImageCropperDialog from '../../../../../components/ImageCropperDialog';
import SolutionPropertyDefinitionModal from './SolutionPropertyDefinitionModal';
import { AreaField } from '../../../../../components/formFields/AreaField';
import { Button } from '@/components/ui/Button';
import MaterialParametersDialog from './patternGallery/MaterialParametersDialog';
import CombinedPatternPreview from './patternGallery/CombinedPatternPreview';
import { useMaterialPattern } from '../../hooks/api';
import {
  PatternPreviewData,
  SolutionImage,
  PropertyDefinition,
  TechnicalReportItem,
  ProfessionBond,
  ReferenceProduct,
  SolutionFormValues,
  SolutionModalProps,
} from '../../types';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogClose,
} from '@/components/ui/Dialog';

const PatternPreviewInline = ({ data }: { data: PatternPreviewData }) => {
  // If we have raw patterns from dialog, use them directly
  const hasRawPatterns = data.fgPattern || data.bgPattern;

  // Only use the hook when we need to load from API (materialPattern with file URLs)
  const { foregroundPattern, backgroundPattern, loadingPatterns } = useMaterialPattern(
    hasRawPatterns ? null : data.materialPattern
  );

  const fg = hasRawPatterns ? data.fgPattern : foregroundPattern;
  const bg = hasRawPatterns ? data.bgPattern : backgroundPattern;

  if (!hasRawPatterns && loadingPatterns) {
    return (
      <div className="flex items-center justify-center h-full">
        <span className="text-xs text-neutral-500">Načítání...</span>
      </div>
    );
  }

  return (
    <CombinedPatternPreview
      foreground={fg}
      foregroundColor={data.fgColor}
      foregroundScale={data.fgScale}
      background={bg}
      backgroundColor={data.bgColor}
      backgroundScale={data.bgScale}
    />
  );
};

function SolutionModalForm({ visible, title, formProps, onSubmit, onClose }: SolutionModalProps) {
  const [visibleAddFileDialog, setVisibleAddFileDialog] = useState(false);
  const [visibleAddPropertyItem, setVisibleAddPropertyItem] = useState(false);
  const [visibleAddTechnicalReportItem, setVisibleAddTechnicalReportItem] = useState(false);
  const [visibleAddPropertyProfessionBond, setVisibleAddProfessionBond] = useState(false);
  const [visibleAddReferenceProduct, setVisibleAddReferenceProduct] = useState(false);
  const [visiblePropertyDefinitionValueSetter, setVisiblePropertyDefinitionValueSetter] =
    useState(false);
  const [visiblePatternDialog, setVisiblePatternDialog] = useState(false);

  const [propertyIdForSetValue, setPropertyIdForSetValue] = useState<string | null>(null);
  const [patternDialogInitial, setPatternDialogInitial] = useState<any>(null);
  const [patternPreview, setPatternPreview] = useState<PatternPreviewData | null>(null);

  useEffect(() => {
    if (formProps?.material_pattern) {
      setPatternPreview({
        fgColor: formProps.material_pattern.fg_color_hex || '#000000',
        bgColor: formProps.material_pattern.bg_color_hex || '#FFFFFF',
        fgScale: formProps.material_pattern.fg_scale,
        bgScale: formProps.material_pattern.bg_scale,
        materialPattern: formProps.material_pattern,
      });
    } else {
      setPatternPreview(null);
    }
    setPatternDialogInitial(null);
  }, [formProps?.material_pattern]);

  const ValidationSchema = Yup.object().shape({
    title: Yup.string().nullable().required('Povinné pole'),
    form: Yup.string().nullable().required('Povinné pole'),
    function: Yup.string().nullable().required('Povinné pole'),
    execution: Yup.string().nullable().required('Povinné pole'),
  });

  const pickerSession = useId();
  const formRef = useRef<any>(null);

  const handleAddFile = (image: SolutionImage) => {
    formRef.current.setFieldValue('images', [...formRef.current.values.images, image]);
    setVisibleAddFileDialog(false);
  };

  const handleDeleteFile = (imageToDelete: SolutionImage) => {
    if (!formRef?.current) return null;
    const newImages = formRef.current.values.images.filter(
      (img: SolutionImage) => img.id !== imageToDelete.id
    );
    formRef.current.setFieldValue('images', newImages);
  };

  const handleDeletePattern = () => {
    setPatternPreview(null);
    setPatternDialogInitial(null);
  };

  const handleAddPropertyItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'property_definition_set',
      mergePickerItems(formRef.current.values.property_definition_set, items)
    );
  };

  const handleAddTechnicalReportItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'technical_report_item_set',
      mergePickerItems(formRef.current.values.technical_report_item_set, items)
    );
  };

  const handleAddProfessionBondItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'profession_bond_set',
      mergePickerItems(formRef.current.values.profession_bond_set, items)
    );
  };

  const handleTechnicalReportItem = (id: string) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.technical_report_item_set.filter(
      (item: TechnicalReportItem) => item.id !== id
    );

    formRef.current.setFieldValue('technical_report_item_set', newValues);
  };

  const handleDeleteProfessionBondItem = (id: string) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.profession_bond_set.filter(
      (item: ProfessionBond) => item.id !== id
    );

    formRef.current.setFieldValue('profession_bond_set', newValues);
  };

  const handleAddReferenceProductItem = async (items: PickerItem[]) => {
    await formRef.current.setFieldValue(
      'reference_product_set',
      mergePickerItems(formRef.current.values.reference_product_set, items)
    );
  };

  const handleDeleteReferenceProductItem = (id: string) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.reference_product_set.filter(
      (item: ReferenceProduct) => item.id !== id
    );

    formRef.current.setFieldValue('reference_product_set', newValues);
  };

  const handleDeletePropertySet = (id: string) => {
    if (!formRef?.current) return null;

    const newValues = formRef.current.values.property_definition_set.filter(
      (item: PropertyDefinition) => item.id !== id
    );

    formRef.current.setFieldValue('property_definition_set', newValues);
  };

  const handleUpdatePropertyValue = (values: Partial<PropertyDefinition>) => {
    if (!formRef?.current || !propertyIdForSetValue) return null;

    const newValues = updateObjectInArray(formRef.current.values.property_definition_set, {
      id: propertyIdForSetValue,
      ...values,
    });

    formRef.current.setFieldValue('property_definition_set', newValues);
    setVisiblePropertyDefinitionValueSetter(false);
    setPropertyIdForSetValue(null);
  };

  const onSubmitButton = () => {
    if (!formRef?.current) return null;
    formRef.current.submitForm();
  };

  return (
    <Dialog open={visible} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[1400px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Formik<SolutionFormValues>
            innerRef={formRef}
            enableReinitialize
            validationSchema={ValidationSchema}
            initialValues={{
              title: formProps?.title || null,
              function: formProps?.function || null,
              form: formProps?.form || null,
              execution: formProps?.execution || null,
              images: formProps?.images || [],
              property_definition_set: formProps?.property_definition_set || [],
              technical_report_item_set: formProps?.technical_report_item_set || [],
              profession_bond_set: formProps?.profession_bond_set || [],
              reference_product_set: formProps?.reference_product_set || [],
            }}
            onSubmit={(values) => {
              if (!formRef.current.dirty) {
                onClose();
                return;
              }

              const submitData: any = {
                ...values,
                images: values.images.filter(Boolean).map((image) => image.id),
                property_definition_set: values.property_definition_set
                  .filter(Boolean)
                  .map((item) => ({
                    id: item.id,
                    value: item.value || null,
                  })),
                technical_report_item_set: values.technical_report_item_set
                  .filter(Boolean)
                  .map((item) => ({
                    id: item.id,
                  })),
                profession_bond_set: values.profession_bond_set.filter(Boolean).map((item) => ({
                  id: item.id,
                })),
                reference_product_set: values.reference_product_set.filter(Boolean).map((item) => ({
                  id: item.id,
                })),
              };

              if (patternDialogInitial) {
                submitData.material_pattern = {
                  fg_pattern: patternDialogInitial.foreground.pattern?.id || null,
                  bg_pattern: patternDialogInitial.background.pattern?.id || null,
                  fg_color_hex: patternDialogInitial.foreground.color,
                  bg_color_hex: patternDialogInitial.background.color,
                  fg_scale: patternDialogInitial.foreground.scale,
                  bg_scale: patternDialogInitial.background.scale,
                };
              }

              onSubmit(formProps?.id, submitData);
            }}
          >
            {(props) => {
              const { values } = props;
              return (
                <Form className="flex">
                  <div className="flex flex-col space-y-1 min-w-96">
                    <div>
                      <Label name="title" title="Název" required />
                      <Field
                        name="title"
                        value={values.title || null}
                        placeholder="Název"
                        component={InputField}
                        data-testid="solution-modal-title-input"
                      />
                    </div>

                    <div>
                      <Label name="function" title="Funkce" required />
                      <Field
                        name="function"
                        value={values.function || null}
                        placeholder="Funkce"
                        component={AreaField}
                        data-testid="solution-modal-function-input"
                      />
                    </div>

                    <div>
                      <Label name="execution" title="Provedení" required />
                      <Field
                        name="execution"
                        value={values.execution || null}
                        placeholder="Provedení"
                        component={AreaField}
                        data-testid="solution-modal-execution-input"
                      />
                    </div>

                    <div>
                      <Label name="form" title="Forma" required />
                      <Field
                        name="form"
                        value={values.form || null}
                        placeholder="Forma"
                        component={AreaField}
                        data-testid="solution-modal-form-input"
                      />
                    </div>
                  </div>

                  <div className="flex flex-col grow ml-4">
                    {/* Obrázky */}
                    <div className="mb-1">
                      <Bar
                        text="Obrázky"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddFileDialog(true)}
                            data-testid="solution-modal-add-image-btn"
                          />,
                        ]}
                      />
                      {values.images && values.images.length > 0 && (
                        <div className="flex flex-wrap gap-2 p-1">
                          {values.images.map((image, i) => (
                            <div key={image.id || i} className="relative group">
                              <img
                                src={image.url || image.image || image.file}
                                alt={image.title || `Obrázek ${i + 1}`}
                                className="size-20 object-cover border border-neutral-200"
                              />
                              <button
                                type="button"
                                className="absolute -top-1.5 -right-1.5 size-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                onClick={() => handleDeleteFile(image)}
                                data-testid={`solution-modal-delete-image-btn-${image.id || i}`}
                              >
                                <Dismiss16Regular className="size-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Vzor */}
                    <div className="mb-1">
                      <Bar
                        text="Vzor"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisiblePatternDialog(true)}
                            data-testid="solution-modal-add-pattern-btn"
                          />,
                        ]}
                      />
                      {patternPreview && (
                        <div
                          className="relative group cursor-pointer"
                          onClick={() => setVisiblePatternDialog(true)}
                          data-testid="solution-modal-pattern-preview"
                        >
                          <div className="w-full h-28 border border-neutral-200 overflow-hidden bg-color4">
                            <PatternPreviewInline data={patternPreview} />
                          </div>
                          <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                            <Pencil className="h-5 w-5 text-white" />
                          </div>
                          <button
                            type="button"
                            className="absolute top-1 right-1 size-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePattern();
                            }}
                            data-testid="solution-modal-delete-pattern-btn"
                          >
                            <Dismiss16Regular className="size-3" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Vlastnosti */}
                    <div className="mb-1">
                      <Bar
                        text="Vlastnosti"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddPropertyItem(true)}
                            data-testid="solution-modal-add-property-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.property_definition_set}
                        withValue
                        onEdit={(id: any) => {
                          setPropertyIdForSetValue(id);
                          setVisiblePropertyDefinitionValueSetter(true);
                        }}
                        onDelete={handleDeletePropertySet}
                      />
                    </div>

                    {/* Prvky technické zprávy */}
                    <div className="mb-1">
                      <Bar
                        text="Prvky technické zprávy"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddTechnicalReportItem(true)}
                            data-testid="solution-modal-add-tech-report-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.technical_report_item_set}
                        onDelete={handleTechnicalReportItem}
                      />
                    </div>

                    {/* Profesní vazby */}
                    <div className="mb-1">
                      <Bar
                        text="Profesní vazby"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddProfessionBond(true)}
                            data-testid="solution-modal-add-profession-bond-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.profession_bond_set}
                        onDelete={handleDeleteProfessionBondItem}
                      />
                    </div>

                    <div className="mb-1">
                      <Bar
                        text="Výrobky"
                        content={[
                          <Plus
                            key={1}
                            className="h-4 w-4"
                            onClick={() => setVisibleAddReferenceProduct(true)}
                            data-testid="solution-modal-add-reference-product-btn"
                          />,
                        ]}
                      />
                      <FormList
                        data={values.reference_product_set}
                        onDelete={handleDeleteReferenceProductItem}
                      />
                    </div>
                  </div>

                  <ImageCropperDialog
                    visible={visibleAddFileDialog}
                    onSubmit={handleAddFile}
                    url="/api/v1/solution/upload-image/"
                    onClose={() => setVisibleAddFileDialog(false)}
                  />
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddPropertyItem}
                    url="left-panel-property-definition/"
                    pickerUrl="property-definition/left-panel-picker/"
                    alreadySelectedValues={values.property_definition_set}
                    onClose={() => setVisibleAddPropertyItem(false)}
                    onSubmit={handleAddPropertyItem}
                  />
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddTechnicalReportItem}
                    url="left-panel-technical-report-item/"
                    pickerUrl="technical-report-item/left-panel-picker/"
                    onClose={() => setVisibleAddTechnicalReportItem(false)}
                    onSubmit={handleAddTechnicalReportItem}
                    alreadySelectedValues={values.technical_report_item_set}
                  />
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddPropertyProfessionBond}
                    url="left-panel-profession-bond/"
                    pickerUrl="profession-bond/left-panel-picker/"
                    onClose={() => setVisibleAddProfessionBond(false)}
                    onSubmit={handleAddProfessionBondItem}
                    alreadySelectedValues={values.profession_bond_set}
                  />
                  <PickValueWithClassificationModal
                    multiple
                    sessionKey={pickerSession}
                    visible={visibleAddReferenceProduct}
                    url="left-panel-product/"
                    pickerUrl="product/left-panel-picker/"
                    onClose={() => setVisibleAddReferenceProduct(false)}
                    onSubmit={handleAddReferenceProductItem}
                    alreadySelectedValues={values.reference_product_set}
                  />
                  <SolutionPropertyDefinitionModal
                    propertyId={propertyIdForSetValue}
                    visible={visiblePropertyDefinitionValueSetter}
                    onClose={() => {
                      setVisiblePropertyDefinitionValueSetter(false);
                      setPropertyIdForSetValue(null);
                    }}
                    onSubmit={handleUpdatePropertyValue}
                  />
                  <MaterialParametersDialog
                    open={visiblePatternDialog}
                    solutionId={formProps?.id || null}
                    materialPatterns={patternPreview?.materialPattern || null}
                    initialValues={patternDialogInitial}
                    onClose={() => setVisiblePatternDialog(false)}
                    onSubmit={(params) => {
                      setPatternPreview({
                        fgColor: params.foreground.color,
                        bgColor: params.background.color,
                        fgScale: params.foreground.scale,
                        bgScale: params.background.scale,
                        fgPattern: params.foreground.pattern?.pattern || null,
                        bgPattern: params.background.pattern?.pattern || null,
                      });
                      setPatternDialogInitial(params);
                      setVisiblePatternDialog(false);
                    }}
                  />
                </Form>
              );
            }}
          </Formik>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button size="lg" variant="outline" data-testid="solution-modal-close-btn">
              Zavřít
            </Button>
          </DialogClose>
          <Button
            size="lg"
            type="submit"
            onClick={onSubmitButton}
            data-testid="solution-modal-submit-btn"
          >
            Potvrdit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SolutionModal(props: React.ComponentProps<typeof SolutionModalForm>) {
  return props.visible ? (
    <SolutionModalForm key={`${props.formProps?.id ?? 'create'}`} {...props} />
  ) : null;
}

export default SolutionModal;
